import type { ReactNode } from 'react';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { formatNumber } from 'components/util/HoursDisplay';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { MeetingPrepMissingValue } from './MeetingPrepMissingValue';

/** Strings meaning "nothing here": empty, either dash, and the backend's `Not recorded` literal. Never `0`, which is a fact. */
const PLACEHOLDER_VALUES = new Set(['', '-', '—', 'Not recorded']);

/** Exported so the header strip's facts and segment chip reach the same verdict as `InfoRow`. */
export function isMissingValue(value: string): boolean {
    return PLACEHOLDER_VALUES.has(value.trim());
}

/**
 * Space-grouped, whole units, no currency code, matching how money reads elsewhere on this page.
 * A `null` value takes the placeholder rather than a formatted `0`, which would claim nothing billed.
 */
export function MoneyFact({ value }: { value: number | null }): ReactNode {
    if (value === null) {
        return <MeetingPrepMissingValue />;
    }
    return <MoneyDisplay amount={value} decimalScale={0} />;
}

/** Pinned to one decimal rather than `useTimecardPrecision`: the brief is a fixed document, not a live table. */
export function formatBriefHours(value: number): string {
    const [integerPart, decimalPart] = formatNumber(value, 1).split('.');
    const groupedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${groupedIntegerPart}.${decimalPart}${t('time.hours-suffix')}`;
}

/** Carries the `h` unit so a bare number can't be read as money; `null` takes the placeholder. */
export function HoursFact({ value }: { value: number | null }): ReactNode {
    if (value === null) {
        return <MeetingPrepMissingValue />;
    }
    return <span className="whitespace-nowrap tabular-nums">{formatBriefHours(value)}</span>;
}

function isPlaceholderValue(children: ReactNode): boolean {
    return typeof children === 'string' && isMissingValue(children);
}

interface InfoRowProps {
    label: ReactNode;
    children: ReactNode;
    /** Spans the full grid width at every breakpoint, for facts too wide to share a row (chip rows, groups). */
    wide?: boolean;
}

export function InfoRow({ label, children, wide = false }: InfoRowProps): ReactNode {
    const isMissing = isPlaceholderValue(children);
    return (
        <div className={cn('flex min-w-0 flex-col gap-0.5', wide && 'sm:col-span-2 md:col-span-3')}>
            <span className="text-muted-foreground text-sm">{label}</span>
            <span className={cn('text-sm', isMissing ? 'text-muted-foreground' : 'font-medium')}>
                {isMissing ? <MeetingPrepMissingValue /> : children}
            </span>
        </div>
    );
}
